import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InboxIcon from '@mui/icons-material/Inbox';
import ForumIcon from '@mui/icons-material/Forum';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useComposeTabsStore } from '../../stores/composeTabsStore';

const GlobalComposeTabs: React.FC = () => {
  console.log('[GlobalComposeTabs] Component rendering');
  const navigate = useNavigate();
  const location = useLocation();
  const { tabs, activeTabId, setActiveTab, closeTab, createTab, showingCompose, chatOpen, viewingChat, openChat, closeChat, setViewingChat, docsOpen, viewingDocs, closeDocs, setViewingDocs, sheetsOpen, viewingSheets, closeSheets, setViewingSheets, aiOpen, viewingAi, closeAi, setViewingAi, panelOrder } = useComposeTabsStore();

  // Debug logging
  React.useEffect(() => {
    console.log('[GlobalComposeTabs] chatOpen:', chatOpen, 'viewingChat:', viewingChat, 'showingCompose:', showingCompose, 'tabs:', tabs.length);
  }, [chatOpen, viewingChat, showingCompose, tabs.length]);

  const isOnMailPage = location.pathname.startsWith('/mail');

  // Don't show if nothing to show
  if (tabs.length === 0 && !isOnMailPage && !chatOpen && !docsOpen && !sheetsOpen && !aiOpen) {
    return null;
  }

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleMailTabClick = () => {
    if (showingCompose) {
      // hide compose, go back to mail view without navigating
      useComposeTabsStore.getState().setShowingCompose(false);
    }
    // Switch to mail view without closing chat tab
    setViewingChat(false);
    setViewingDocs(false);
    setViewingSheets(false);
    navigate('/mail/inbox');
  };

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    closeTab(tabId);
  };

  const handleAddTab = () => {
    createTab();
  };

  const getTabTitle = (tab: typeof tabs[0]) => {
    if (tab.subject?.trim()) {
      return tab.subject.length > 22 ? tab.subject.substring(0, 22) + '…' : tab.subject;
    }
    return 'No Subject';
  };

  // Has unsaved content?
  const hasContent = (tab: typeof tabs[0]) =>
    tab.to.length > 0 || tab.subject.trim() || tab.body.replace(/<[^>]*>/g, '').trim();

  // V4-A: Slash Indigo (Superhuman style)
  const INDIGO = '#4f46e5';
  const textActive = INDIGO;
  const textIdle = '#c0c0c0';
  const iconIdle = '#d4d4d4';

  const tabSx = (isActive: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    px: '12px',
    height: 38,
    cursor: 'pointer',
    userSelect: 'none' as const,
    position: 'relative' as const,
    bgcolor: 'transparent',
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    transition: 'color 0.15s',
    '&::after': isActive ? {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '12px',
      right: '12px',
      height: '2px',
      bgcolor: INDIGO,
      borderRadius: '2px 2px 0 0',
      display: 'block',
    } : { display: 'none' },
    '&:hover': isActive ? {} : { opacity: 0.7 },
  });

  const sepEl = (key: string) => (
    <Box key={key} component="span" sx={{ color: '#e2e2e2', fontSize: '14px', userSelect: 'none', flexShrink: 0, lineHeight: '38px' }}>/</Box>
  );

  // Build tab list with separators
  const isMailActive = !showingCompose && (!chatOpen || !viewingChat) && !viewingDocs && !viewingSheets && !viewingAi;
  const tabItems: React.ReactNode[] = [];

  // Mail tab
  tabItems.push(
    <Box key="mail" onClick={handleMailTabClick} sx={tabSx(isMailActive)}>
      <InboxIcon sx={{ fontSize: 15, color: isMailActive ? textActive : iconIdle, flexShrink: 0, transition: 'color 0.15s' }} />
      <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: isMailActive ? 600 : 500, color: isMailActive ? textActive : textIdle, letterSpacing: '-0.01em', transition: 'color 0.15s' }}>
        Mail
      </Typography>
    </Box>
  );

  tabs.forEach((tab) => {
    const isActive = showingCompose && activeTabId === tab.id;
    const dirty = hasContent(tab);
    tabItems.push(sepEl(`sep-${tab.id}`));
    tabItems.push(
      <Tooltip key={tab.id} title={tab.subject?.trim() || 'No Subject'} placement="bottom" arrow>
        <Box onClick={() => handleTabClick(tab.id)} sx={tabSx(isActive)}>
          {dirty && !isActive
            ? <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: INDIGO, flexShrink: 0 }} />
            : <EditIcon sx={{ fontSize: 14, color: isActive ? textActive : iconIdle, flexShrink: 0, transition: 'color 0.15s' }} />
          }
          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: isActive ? textActive : textIdle, flex: 1, letterSpacing: '-0.01em', transition: 'color 0.15s' }}>
            {getTabTitle(tab)}
          </Typography>
          <IconButton size="small" onClick={(e) => handleCloseTab(e, tab.id)}
            sx={{ p: 0.15, flexShrink: 0, ml: '-2px', color: isActive ? `${INDIGO}66` : iconIdle, '&:hover': { bgcolor: 'rgba(79,70,229,0.08)', color: isActive ? INDIGO : '#666' } }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      </Tooltip>
    );
  });

  // Render panels in the order they were opened
  panelOrder.forEach((panel) => {
    if (panel === 'chat' && chatOpen) {
      const chatActive = viewingChat && !showingCompose;
      tabItems.push(sepEl('sep-chat'));
      tabItems.push(
        <Box key="chat" onClick={() => setViewingChat(true)} sx={tabSx(chatActive)}>
          <ForumIcon sx={{ fontSize: 14, color: chatActive ? textActive : iconIdle, flexShrink: 0, transition: 'color 0.15s' }} />
          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: chatActive ? 600 : 500, color: chatActive ? textActive : textIdle, letterSpacing: '-0.01em', transition: 'color 0.15s' }}>
            Team Chat
          </Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); closeChat(); }}
            sx={{ p: 0.15, flexShrink: 0, ml: '-2px', color: chatActive ? `${INDIGO}66` : iconIdle, '&:hover': { bgcolor: 'rgba(79,70,229,0.08)', color: chatActive ? INDIGO : '#666' } }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      );
    } else if (panel === 'docs' && docsOpen) {
      const docActive = viewingDocs && !showingCompose;
      const docColor = '#1a73e8';
      tabItems.push(sepEl('sep-docs'));
      tabItems.push(
        <Box key="docs" onClick={() => setViewingDocs(true)} sx={tabSx(docActive)}>
          <DescriptionIcon sx={{ fontSize: 14, color: docActive ? docColor : iconIdle, flexShrink: 0, transition: 'color 0.15s' }} />
          <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: docActive ? 600 : 500, color: docActive ? docColor : textIdle, letterSpacing: '-0.01em', transition: 'color 0.15s' }}>
            Documents
          </Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); closeDocs(); }}
            sx={{ p: 0.15, flexShrink: 0, ml: '-2px', color: docActive ? `${docColor}66` : iconIdle, '&:hover': { bgcolor: 'rgba(26,115,232,0.08)', color: docActive ? docColor : '#666' } }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      );
    } else if (panel === 'sheets' && sheetsOpen) {
      const sheetActive = viewingSheets && !showingCompose;
      const sheetColor = '#0f9d58';
      tabItems.push(sepEl('sep-sheets'));
      tabItems.push(
        <Box key="sheets" onClick={() => setViewingSheets(true)} sx={tabSx(sheetActive)}>
          <TableChartIcon sx={{ fontSize: 14, color: sheetActive ? sheetColor : iconIdle, flexShrink: 0, transition: 'color 0.15s' }} />
          <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: sheetActive ? 600 : 500, color: sheetActive ? sheetColor : textIdle, letterSpacing: '-0.01em', transition: 'color 0.15s' }}>
            Spreadsheets
          </Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); closeSheets(); }}
            sx={{ p: 0.15, flexShrink: 0, ml: '-2px', color: sheetActive ? `${sheetColor}66` : iconIdle, '&:hover': { bgcolor: 'rgba(15,157,88,0.08)', color: sheetActive ? sheetColor : '#666' } }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      );
    } else if (panel === 'ai' && aiOpen) {
      const aiActive = viewingAi && !showingCompose;
      const aiColor = '#7c3aed';
      tabItems.push(sepEl('sep-ai'));
      tabItems.push(
        <Box key="ai" onClick={() => setViewingAi(true)} sx={tabSx(aiActive)}>
          <AutoAwesomeIcon sx={{ fontSize: 14, color: aiActive ? aiColor : iconIdle, flexShrink: 0, transition: 'color 0.15s' }} />
          <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: aiActive ? 600 : 500, color: aiActive ? aiColor : textIdle, letterSpacing: '-0.01em', transition: 'color 0.15s' }}>
            AI Assistant
          </Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); closeAi(); }}
            sx={{ p: 0.15, flexShrink: 0, ml: '-2px', color: aiActive ? `${aiColor}66` : iconIdle, '&:hover': { bgcolor: 'rgba(124,58,237,0.08)', color: aiActive ? aiColor : '#666' } }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      );
    }
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#ffffff',
        borderBottom: '1px solid #e8e8e8',
        minHeight: 38,
        px: '14px',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {tabItems}

      {/* New compose button */}
      <Tooltip title="New compose" placement="bottom" arrow>
        <IconButton
          size="small"
          onClick={handleAddTab}
          sx={{
            ml: 0.5,
            p: 0.5,
            color: '#ccc',
            '&:hover': { bgcolor: 'rgba(79,70,229,0.06)', color: INDIGO },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default GlobalComposeTabs;
